import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Sliders } from 'lucide-react';

const MatchingWeights = () => {
  const [weights, setWeights] = useState({
    readiness_weight: 0.4,
    interests_weight: 0.3,
    location_weight: 0.2,
    experience_weight: 0.1
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchWeights();
  }, []);

  const fetchWeights = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/matching-weights`);
      setWeights(response.data);
    } catch (error) {
      console.error('Failed to fetch weights:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateWeights = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API_BASE}/admin/matching-weights`, weights);
      toast.success('Matching weights updated! All matches recalculated.');
    } catch (error) {
      toast.error('Failed to update weights');
    } finally {
      setUpdating(false);
    }
  };

  const handleSliderChange = (key, value) => {
    setWeights(prev => ({ ...prev, [key]: parseFloat(value) }));
  };

  const totalWeight = Object.values(weights).reduce((sum, val) => sum + val, 0);
  const isValid = Math.abs(totalWeight - 1.0) < 0.01;

  if (loading) {
    return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div></div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Matching Algorithm</h1>
        <p className="text-lg text-slate-600 mb-8">Adjust weights to fine-tune the matching algorithm</p>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-8">
          <div>
            <Label htmlFor="readiness">Readiness Score Weight: {(weights.readiness_weight * 100).toFixed(0)}%</Label>
            <input
              id="readiness"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={weights.readiness_weight}
              onChange={(e) => handleSliderChange('readiness_weight', e.target.value)}
              className="w-full mt-2"
              data-testid="readiness-weight-slider"
            />
            <p className="text-sm text-slate-500 mt-1">How much emphasis on assessment readiness scores</p>
          </div>

          <div>
            <Label htmlFor="interests">Interests Alignment Weight: {(weights.interests_weight * 100).toFixed(0)}%</Label>
            <input
              id="interests"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={weights.interests_weight}
              onChange={(e) => handleSliderChange('interests_weight', e.target.value)}
              className="w-full mt-2"
              data-testid="interests-weight-slider"
            />
            <p className="text-sm text-slate-500 mt-1">How much emphasis on matching interests</p>
          </div>

          <div>
            <Label htmlFor="location">Location Proximity Weight: {(weights.location_weight * 100).toFixed(0)}%</Label>
            <input
              id="location"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={weights.location_weight}
              onChange={(e) => handleSliderChange('location_weight', e.target.value)}
              className="w-full mt-2"
              data-testid="location-weight-slider"
            />
            <p className="text-sm text-slate-500 mt-1">How much emphasis on geographic proximity</p>
          </div>

          <div>
            <Label htmlFor="experience">Experience Weight: {(weights.experience_weight * 100).toFixed(0)}%</Label>
            <input
              id="experience"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={weights.experience_weight}
              onChange={(e) => handleSliderChange('experience_weight', e.target.value)}
              className="w-full mt-2"
              data-testid="experience-weight-slider"
            />
            <p className="text-sm text-slate-500 mt-1">How much emphasis on school year/experience</p>
          </div>

          <div className="pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-600">Total Weight:</span>
              <span className={`font-bold ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                {(totalWeight * 100).toFixed(0)}%
              </span>
            </div>
            {!isValid && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-sm text-orange-700">
                ⚠️ Warning: Total weights must equal 100%
              </div>
            )}
            <Button
              onClick={updateWeights}
              disabled={!isValid || updating}
              className="w-full bg-[#0D9488] hover:bg-[#115E59] rounded-full"
              data-testid="update-weights-btn"
            >
              <Sliders className="mr-2 w-4 h-4" />
              {updating ? 'Updating...' : 'Update Weights & Recalculate Matches'}
            </Button>
          </div>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
          <p className="text-sm text-blue-800">
            When you update these weights, all existing matches will be recalculated based on the new algorithm. 
            This allows you to optimize matching based on what matters most for your platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MatchingWeights;
